import { SurveyAnswers } from "../../common/hooks/api/useSurvey";
import { OrganizationDataRecipientDto } from "../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationManagedAdNetworkDto } from "../../common/service/server/dto/OrganizationManagedAdNetworkDto";
import {
  CUSTOM_AUDIENCE_VENDOR_FEATURE_SLUG,
  CUSTOM_AUDIENCE_VENDOR_MANAGED_SHARING_VENDORS_FEATURE_SLUG,
  DIRECT_MAIL_VENDORS_SLUG,
} from "../surveys/sharingSurvey";
import { parseMultiselectAnswerWithNoneOption } from "../surveys/survey";

type OptOutWorkflowVendorSelection = {
  directMailVendorIds: string[];
  managerVendorIds: string[];
  managedVendorIds: string[];
  hasManagedNetworks: boolean;
};

const parseStringArray = (rawValue: string | null | undefined): string[] => {
  try {
    const parsed = JSON.parse(rawValue || "[]");
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch (e) {
    return [];
  }
};

// FUTURE: After unification (probably), we should update RemoveConsumer.tsx to use this shared logic for determining
// which vendors appear in the opt-out workflow. For now this is only used by the setup page, but I made it in a utility
// file so we can share it in the future. (It's essentially a copy of what's in RemoveConsumer.tsx). Wanted to keep things
// low-stakes for this automation-focused PR, so holding off on refactoring the opt-out workflow here.
export const getOptOutWorkflowVendorSelection = (
  answers: SurveyAnswers,
  managedAdNetworks?: OrganizationManagedAdNetworkDto[],
): OptOutWorkflowVendorSelection => {
  const managerVendorIds = parseStringArray(answers?.[CUSTOM_AUDIENCE_VENDOR_FEATURE_SLUG]).filter(
    (id) => id !== "none",
  );

  const directMailVendorIds = parseMultiselectAnswerWithNoneOption(
    answers,
    DIRECT_MAIL_VENDORS_SLUG,
  );

  if (managedAdNetworks) {
    const managedVendorIds = [
      ...new Set(
        managedAdNetworks
          .filter((item) => managerVendorIds.includes(item.managerId))
          .map((item) => item.networkId),
      ),
    ];

    return {
      directMailVendorIds,
      managerVendorIds,
      managedVendorIds,
      hasManagedNetworks: managedVendorIds.length > 0,
    };
  }

  return {
    directMailVendorIds,
    managerVendorIds,
    managedVendorIds: parseStringArray(
      answers?.[CUSTOM_AUDIENCE_VENDOR_MANAGED_SHARING_VENDORS_FEATURE_SLUG],
    ),
    hasManagedNetworks: true,
  };
};

export const isVendorIncludedInOptOutWorkflow = (
  vendor: OrganizationDataRecipientDto,
  selection: OptOutWorkflowVendorSelection,
) => {
  const isManagedByAnotherVendor = selection.managedVendorIds.includes(vendor.id);
  const isIncludedByTypeLikeRemoveConsumer =
    vendor.isUploadVendor ||
    vendor.isAlwaysUploadVendor ||
    vendor.usesCustomAudience ||
    selection.directMailVendorIds.includes(vendor.id);

  const isSelectedManagerVendor =
    selection.hasManagedNetworks && selection.managerVendorIds.includes(vendor.id);

  return (
    (isIncludedByTypeLikeRemoveConsumer || isSelectedManagerVendor) && !isManagedByAnotherVendor
  );
};
